import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, Plus } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { useMyProjects, useSetProjectStatus } from '@/lib/projects';
import { PROJECT_TYPE_LABEL } from '@/lib/project-types';
import { PageHeader } from '@/components/page-header';
import { PickClientDialog } from '@/components/pick-client-dialog';
import { ProjectStatusSelect } from '@/components/project-status-select';

export function ProjectsIndex() {
  const [page, setPage] = useState(1);
  const [pickOpen, setPickOpen] = useState(false);
  const { data: paginated, isLoading } = useMyProjects(page);
  const setStatus = useSetProjectStatus();
  const data = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FolderKanban}
        title="Projects"
        subtitle="Active work across all clients."
        action={
          <Button onClick={() => setPickOpen(true)}>
            <Plus className="size-4" /> New project
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Pick a client to start the first project."
          action={
            <Button onClick={() => setPickOpen(true)}>
              <Plus className="size-4" /> New project
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`}>
                <Card className="h-full transition-colors hover:bg-[var(--color-surface-muted)]/40">
                  <CardHeader>
                    <div className="text-xs text-[var(--color-foreground-subtle)]">{p.client_name}</div>
                    <div className="mt-1 font-medium">{p.name}</div>
                  </CardHeader>
                  <CardBody className="flex items-center justify-between text-xs text-[var(--color-foreground-muted)]">
                    <div className="flex items-center gap-1.5">
                      <Badge tone="neutral">{PROJECT_TYPE_LABEL[p.type] ?? p.type}</Badge>
                      <ProjectStatusSelect
                        value={p.status}
                        insideLink
                        disabled={setStatus.isPending}
                        onChange={(status) => setStatus.mutate({ id: p.id, status })}
                      />
                    </div>
                    <span>{p.cards_count} card{p.cards_count === 1 ? '' : 's'}</span>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}
      <PickClientDialog
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title="New project"
        tab="projects"
      />
    </div>
  );
}
